import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { initPwaInstall } from '@/composables/usePwaInstall'
import { registerServiceWorker } from '@/composables/registerServiceWorker'

// PrimeVue core
import PrimeVue from 'primevue/config'
import { ImmigReerPreset } from './theme/preset'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import StyleClass from 'primevue/styleclass'
import Tooltip from 'primevue/tooltip'

// Tour guidato — v-tour-guide (no app.use() required — usato come componente TourGuideManager)
import 'v-tour-guide/style.css'

// Stili globali — Tailwind v4 + SCSS layout Ultima + primeicons
import './assets/tailwind.css'
import './assets/styles.scss'
// Alias superfici adattive al tema (--surf-*) + .field — DOPO Tailwind e Ultima
import './styles/global.css'

// PrimeVue components (registrati globalmente — usati sia nel layout che nelle view)
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import InputOtp from 'primevue/inputotp'
import Card from 'primevue/card'
import Message from 'primevue/message'
import Toast from 'primevue/toast'
import Menu from 'primevue/menu'
import MegaMenu from 'primevue/megamenu'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Select from 'primevue/select'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import Badge from 'primevue/badge'
import OverlayBadge from 'primevue/overlaybadge'
import Skeleton from 'primevue/skeleton'
import Chart from 'primevue/chart'
import Drawer from 'primevue/drawer'
import ToggleSwitch from 'primevue/toggleswitch'
import InputNumber from 'primevue/inputnumber'
import Chip from 'primevue/chip'
import Menubar from 'primevue/menubar'
import SelectButton from 'primevue/selectbutton'
import RadioButton from 'primevue/radiobutton'
import Breadcrumb from 'primevue/breadcrumb'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import ConfirmDialog from 'primevue/confirmdialog'
import AutoComplete from 'primevue/autocomplete'
import DatePicker from 'primevue/datepicker'
import Textarea from 'primevue/textarea'
import ProgressSpinner from 'primevue/progressspinner'
import ProgressBar from 'primevue/progressbar'
import FileUpload from 'primevue/fileupload'
import Rating from 'primevue/rating'
import Checkbox from 'primevue/checkbox'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import Panel from 'primevue/panel'
import Fieldset from 'primevue/fieldset'
import Toolbar from 'primevue/toolbar'
import ToggleButton from 'primevue/togglebutton'
import Divider from 'primevue/divider'
import Paginator from 'primevue/paginator'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import Popover from 'primevue/popover'
import SplitButton from 'primevue/splitbutton'
import Tree from 'primevue/tree'
import MultiSelect from 'primevue/multiselect'

import App from './App.vue'
import router from './router'

// i18n (DB-backed): bundle caricati a runtime dall'API, lingua iniziale da localStorage
import { i18n, storedLocale, hydrateFromCache, loadLocaleMessages } from './i18n'
import { PRIMEVUE_LOCALES } from './i18n/primevueLocales'

const app = createApp(App)

// Lingua iniziale: idrata i messaggi dalla cache localStorage (primo paint senza flash)
const initialLocale = storedLocale()
hydrateFromCache(initialLocale)

// Plugin
app.use(createPinia())
app.use(router)
app.use(PrimeVue, {
  theme: {
    preset: ImmigReerPreset,
    options: {
      darkModeSelector: '.app-dark',
      cssLayer: false,
    },
  },
  // Locale PrimeVue iniziale (DataTable/Calendar/ecc.), poi aggiornato da useLocale al cambio lingua
  locale: { ...PRIMEVUE_LOCALES[initialLocale] },
})
app.use(ToastService)
app.use(ConfirmationService)
app.use(i18n)

// Direttive
app.directive('styleclass', StyleClass)
app.directive('tooltip', Tooltip)

// Registrazione componenti globali PrimeVue
app.component('Button', Button)
app.component('InputText', InputText)
app.component('Password', Password)
app.component('InputOtp', InputOtp)
app.component('Card', Card)
app.component('Message', Message)
app.component('Toast', Toast)
app.component('Menu', Menu)
app.component('MegaMenu', MegaMenu)
app.component('DataTable', DataTable)
app.component('Column', Column)
app.component('Select', Select)
app.component('Tag', Tag)
app.component('Dialog', Dialog)
app.component('Badge', Badge)
app.component('OverlayBadge', OverlayBadge)
app.component('Skeleton', Skeleton)
app.component('Chart', Chart)
app.component('Drawer', Drawer)
app.component('ToggleSwitch', ToggleSwitch)
app.component('InputNumber', InputNumber)
app.component('Chip', Chip)
app.component('Menubar', Menubar)
app.component('SelectButton', SelectButton)
app.component('RadioButton', RadioButton)
app.component('Breadcrumb', Breadcrumb)
app.component('IconField', IconField)
app.component('InputIcon', InputIcon)
app.component('ConfirmDialog', ConfirmDialog)
app.component('AutoComplete', AutoComplete)
app.component('DatePicker', DatePicker)
app.component('Textarea', Textarea)
app.component('ProgressSpinner', ProgressSpinner)
app.component('ProgressBar', ProgressBar)
app.component('FileUpload', FileUpload)
app.component('Rating', Rating)
app.component('Checkbox', Checkbox)
app.component('Accordion', Accordion)
app.component('AccordionTab', AccordionTab)
app.component('AccordionPanel', AccordionPanel)
app.component('AccordionHeader', AccordionHeader)
app.component('AccordionContent', AccordionContent)
app.component('Panel', Panel)
app.component('Fieldset', Fieldset)
app.component('Toolbar', Toolbar)
app.component('ToggleButton', ToggleButton)
app.component('Divider', Divider)
app.component('Paginator', Paginator)
app.component('Tabs', Tabs)
app.component('TabList', TabList)
app.component('Tab', Tab)
app.component('TabPanels', TabPanels)
app.component('TabPanel', TabPanel)
app.component('Popover', Popover)
app.component('SplitButton', SplitButton)
app.component('Tree', Tree)
app.component('MultiSelect', MultiSelect)

// Carica il bundle traduzioni aggiornato dall'API, poi monta (la cache idratata sopra
// garantisce comunque un primo paint immediato per gli utenti di ritorno).
// IMPORTANTE: attendere router.isReady() prima del mount, così la navigazione iniziale
// (e la guardia auth async: ensureLoaded → /auth/me) è già risolta al primo render.
// Senza, su un caricamento diretto la route è ancora la START_LOCATION (meta vuoto) e
// App.vue renderebbe per un istante <AppLayout> (shell dashboard) prima del redirect a /login.
// PWA: ascolta subito beforeinstallprompt (scatta una volta, prima del montaggio del menu) e
// registra il service worker solo in produzione.
initPwaInstall()
registerServiceWorker()

loadLocaleMessages(initialLocale).finally(async () => {
  await router.isReady()
  app.mount('#app')
})
